import _ from 'lodash';

import { useAppUpdateTracking } from './use-app-update-tracking';
import { useAppLauncherRouting } from './use-app-launcher-routing';
import {
  useAppLauncherStore,
  useAppLauncherSetters,
} from './use-app-launcher-store';
import { useContactsIntegration } from './use-contacts-integration';
import { SDK, APP_STATES } from '../constants';

export const useAppUpdate = () => {
  const store = useAppLauncherStore();
  const setters = useAppLauncherSetters();

  const { checkOrUpdateContacts } = useContactsIntegration();
  const { changeTrackingRate } = useAppUpdateTracking();
  const { goTo } = useAppLauncherRouting();

  /** Start the update process to the latest version */
  const onUpdateToMaxAppVersion = async () => {
    await updateToAppVersion({ vTarget: store.maxVersion });
  };

  /** If there is an error we can retry updating to the same version */
  const onRetryAppUpdate = async () => {
    await updateToAppVersion({ vTarget: store.updateTargetVersion });
  };

  /** Same as update to max version but with user chosen version and shows different loaders */
  const onUpdateToTargetAppVersion = async () => {
    try {
      const vCurrent = store.settingsInDB.appVersion;
      const vTarget = store.settings.appVersion;
      store.isPreparingUpdate = true;

      await initializeDataUpdate({ vCurrent, vTarget });

      if (store.isUpdateInProgress) {
        goTo({ state: APP_STATES.APP_IS_UPDATING });
      }

      store.isPreparingUpdate = false;
    } catch (e: any) {
      store.error = e;
      store.isPreparingUpdate = false;
      console.error('Error while initializing an update!', e);
    }
  };

  /** Install is the same as update but with different loading screen */
  const updateToAppVersion = async (args: { vTarget: string; }) => {
    try {
      const { vTarget } = args;
      store.isPreparingInstall = true;
      store.settings = { ...store.settings, appVersion: vTarget };

      await initializeDataUpdate({ vTarget });

      if (store.isUpdateInProgress) {
        goTo({ state: APP_STATES.APP_IS_UPDATING });
      }

      store.isPreparingInstall = false;
    } catch (e: any) {
      store.error = e;
      store.isPreparingInstall = false;
      console.error('Error while initializing an update!', e);
    }
  };

  /** Make a request to start updating to given version */
  const initializeDataUpdate = async (args: {
    vCurrent?: string;
    vTarget: string;
  }) => {
    const { vCurrent, vTarget } = args;

    const mCurrent = store.getMigrationsByVersion(vCurrent);
    const mTarget = store.getMigrationsByVersion(vTarget);
    const mPending = _.difference(mTarget, mCurrent);

    if (!mPending.length) {
      setters.clearUpdateProgress();
      return;
    }

    try {
      await checkOrUpdateContacts({ mPending });
      await SDK.HITL.migrations.deleteMigrationsTableLock();

      const response = await SDK.HITL.migrations.runAllMigrations({
        name: mPending.at(-1),
        method: 'up',
        meta: { appVersion: vTarget },
      });

      changeTrackingRate({ rate: 'update-in-progress' });
      setters.changeUpdateProgress({
        meta: { appVersion: vTarget },
        pending: response.migrations.map(item => item.name),
        complete: [],
        isInProgress: true,
      });
    } catch (e: any) {
      setters.clearUpdateProgress();
      throw e;
    }
  };

  return {
    onUpdateToMaxAppVersion,
    onUpdateToTargetAppVersion,
    onRetryAppUpdate,
    initializeDataUpdate,
  };
};
